import {
  Box,
  chakra,
  Flex,
  HStack,
  Link,
  Text,
  useColorModeValue,
  VisuallyHidden,
} from '@chakra-ui/react';
import { FaLinkedin } from 'react-icons/fa';
import type { ReactNode } from 'react';
import { FooterContact } from './FooterContact';

const SocialButton = ({ children, label, href }: { children: ReactNode; label: string; href: string }) => {
  const iconColor = useColorModeValue('gray.600', 'gray.400');
  // LinkedIn brand blue (light); lighter LinkedIn blue for contrast on dark footer band.
  const iconHoverColor = useColorModeValue('#0A66C2', '#70B5F9');
  const bg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  const bgHover = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');

  return (
    <chakra.button
      bg={bg}
      color={iconColor}
      rounded={'full'}
      w={8}
      h={8}
      cursor={'pointer'}
      as={'a'}
      href={href}
      display={'inline-flex'}
      alignItems={'center'}
      justifyContent={'center'}
      target='_blank'
      rel='noopener noreferrer'
      transition='color 0.15s ease, background-color 0.15s ease'
      _hover={{
        bg: bgHover,
        color: iconHoverColor,
      }}
    >
      <VisuallyHidden>{label}</VisuallyHidden>
      {children}
    </chakra.button>
  );
};

export function Footer() {
  const year = new Date().getFullYear();
  const bandBg = useColorModeValue('#F6F7F9', 'gray.800');
  const borderTopColor = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const linkColor = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  /** Same vertical footprint as `components/rulesMarketing/Footer.tsx` (single band, `py={6}`); social row stays inline with legal links. */
  return (
    <Box
      as='footer'
      w='full'
      bg={bandBg}
      borderTop='1px solid'
      borderColor={borderTopColor}
      py={6}
      px={{ base: 4, md: 8 }}
    >
      <Flex
        maxW='4xl'
        mx='auto'
        align='center'
        justify='center'
        gap={{ base: 2, sm: 4 }}
        flexWrap='wrap'
      >
        <Link
          href='/Privacy_Policy.pdf?v=20260715'
          target='_blank'
          rel='noopener noreferrer'
          fontSize='sm'
          color={linkColor}
          textDecoration='none'
          _hover={{ textDecoration: 'underline' }}
        >
          Privacy Notice
        </Link>
        <FooterContact linkColor={linkColor} />
        <HStack spacing={3} alignItems='center' flexShrink={0}>
          <SocialButton label={'LinkedIn'} href={'https://www.linkedin.com/company/alexandria-software-ltd/'}>
            <FaLinkedin />
          </SocialButton>
        </HStack>
        <Text fontSize='sm' color={mutedColor}>
          © {year} 10 Teams
        </Text>
      </Flex>
    </Box>
  );
}
